import { useCallback, useEffect, useRef, useState } from 'react';

const getSpeechSynthesis = (): SpeechSynthesis | null =>
	typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;

// Left to itself the browser picks any installed German voice, which on macOS
// includes novelty ones (Grandpa, Rocko). Best first, and all of them on-device:
// a cloud voice (Chrome's `Google Deutsch`) synthesizes server-side, so choosing
// one would send the answer to a vendor we have no processing agreement with.
const PREFERRED_VOICE_NAMES = ['Microsoft Katja', 'Microsoft Hedda', 'Helena', 'Anna'];

// macOS and Windows ship a far better variant of their system voices that the user
// has to download first ("Anna (Premium)", "Helena (Enhanced)"); the name is the only
// thing that tells it apart from the dated one it sits next to in the list.
const HIGH_QUALITY_VOICE = /\b(premium|enhanced|natural)\b/i;

const pickGermanVoice = (speechSynthesis: SpeechSynthesis): SpeechSynthesisVoice | null => {
	const germanVoices = speechSynthesis
		.getVoices()
		.filter((voice) => voice.lang.startsWith('de') && voice.localService);

	// Browsers decorate the name they expose ("Microsoft Katja Online (Natural) -
	// German (Germany)"), so a preferred name matches the start of a word, not the
	// whole string.
	const preferredVoices = PREFERRED_VOICE_NAMES.flatMap((name) =>
		germanVoices.filter((voice) => voice.name === name || voice.name.startsWith(`${name} `))
	);

	// A downloaded high-quality voice outranks the preference order, even one we never
	// named: the novelty voices ship no such variant, so the tag alone keeps them out.
	return (
		preferredVoices.find((voice) => HIGH_QUALITY_VOICE.test(voice.name)) ??
		germanVoices.find((voice) => HIGH_QUALITY_VOICE.test(voice.name)) ??
		preferredVoices[0] ??
		null
	);
};

/**
 * Reads a message aloud through the native Web Speech API.
 *
 * `speechSynthesis` is a single global queue, so "only one message is read at a time"
 * is a property of the browser rather than something to coordinate between messages:
 * cancelling before every utterance stops whichever message was speaking, and that
 * message's own `end` handler returns its button to the speaker icon.
 */
const useReadAloud = () => {
	const [isSpeaking, setIsSpeaking] = useState(false);
	// The utterance this message started, while it is still the one being read. It is
	// what makes the single global queue safe to share: every cancel below belongs to
	// this message, and a cancelled utterance's late `end` no longer speaks for the
	// one that replaced it.
	const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

	useEffect(
		() => () => {
			if (utteranceRef.current) {
				utteranceRef.current = null;
				getSpeechSynthesis()?.cancel();
			}
		},
		[]
	);

	const toggle = useCallback((text: string) => {
		const speechSynthesis = getSpeechSynthesis();
		if (!speechSynthesis) {
			return;
		}

		const wasReading = utteranceRef.current !== null;
		utteranceRef.current = null;
		// Cancel in both branches: stopping is a cancel, and starting has to stop
		// whichever other message is being read.
		speechSynthesis.cancel();

		if (wasReading || !text.trim()) {
			setIsSpeaking(false);
			return;
		}

		const utterance = new SpeechSynthesisUtterance(text);
		// Absent on the very first paint, when the browser has yet to load its voice
		// list, and on a machine with no local German voice — the browser's own choice
		// is the fallback, which Chrome may answer with a cloud voice.
		const voice = pickGermanVoice(speechSynthesis);
		if (voice) {
			utterance.voice = voice;
		}
		const finish = () => {
			if (utteranceRef.current !== utterance) {
				return;
			}
			utteranceRef.current = null;
			setIsSpeaking(false);
		};
		utterance.onend = finish;
		utterance.onerror = finish;
		utteranceRef.current = utterance;
		speechSynthesis.speak(utterance);
		setIsSpeaking(true);
	}, []);

	return { isSupported: getSpeechSynthesis() !== null, isSpeaking, toggle };
};

export default useReadAloud;
